// ============================================================
//  SUMMER CAMP DIRECTORY — CAMP LISTINGS
// ============================================================
//  discount → true = show "Mama Preferred" badge (hand-selected favorites
//             or confirmed Mommy Mingle member discount) | false = no badge
// ============================================================

export type AvailabilityStatus =
  | 'Spots Available'
  | 'Limited Spots'
  | 'Waitlist'
  | 'Registration Opens Soon'
  | 'Unknown — Check with Camp';

export interface Camp {
  id: number;
  name: string;
  city: string;
  ageRange: string;
  ageGroup: 'preschool' | 'early-elementary' | 'upper-elementary' | 'tweens' | 'teens';
  category: 'sports' | 'stem' | 'arts' | 'outdoor' | 'faith' | 'special-needs';
  priceRange: string;
  duration: 'full-day' | 'half-day' | 'overnight';
  availableWeeks: number[];
  description: string;
  website: string;
  registrationLink?: string;
  photo: string;
  discount: boolean;
  availability: AvailabilityStatus;
}

// ── Summer Week Schedule ─────────────────────────────────────
export const summerWeeks = [
  { week: 1,  label: 'Week 1',  dates: 'Jun 2–6' },
  { week: 2,  label: 'Week 2',  dates: 'Jun 9–13' },
  { week: 3,  label: 'Week 3',  dates: 'Jun 16–20' },
  { week: 4,  label: 'Week 4',  dates: 'Jun 23–27' },
  { week: 5,  label: 'Week 5',  dates: 'Jun 30–Jul 4' },
  { week: 6,  label: 'Week 6',  dates: 'Jul 7–11' },
  { week: 7,  label: 'Week 7',  dates: 'Jul 14–18' },
  { week: 8,  label: 'Week 8',  dates: 'Jul 21–25' },
  { week: 9,  label: 'Week 9',  dates: 'Jul 28–Aug 1' },
  { week: 10, label: 'Week 10', dates: 'Aug 4–8' },
];

// ── Camp Listings ─────────────────────────────────────────────
export const camps: Camp[] = [

  // ── CAMP 2 ───────────────────────────────────────────────
  {
    id: 2,
    name: "Code Creators Academy",
    city: "Duluth",
    ageRange: "8–13",
    ageGroup: "upper-elementary",
    category: "stem",
    priceRange: "$300–$400/week",
    duration: "full-day",
    availableWeeks: [2, 3, 4, 6, 7, 8, 9],
    description: "Learn coding, robotics, and game design in a fun, hands-on environment with experienced instructors who make complex concepts exciting.",
    website: "https://www.codeninjas.com",
    photo: "https://public.readdy.ai/ai/img_res/703ee84bedcde9f2a6ebe106d7505bcf.jpg",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 4 ───────────────────────────────────────────────
  {
    id: 4,
    name: "Soccer Stars Academy",
    city: "Buford",
    ageRange: "6–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "$225–$325/week",
    duration: "full-day",
    availableWeeks: [1, 3, 5, 7, 9],
    description: "Professional soccer training with certified coaches for all skill levels. Daily drills, scrimmages, and an end-of-week tournament.",
    website: "https://www.soccerstars.com",
    photo: "https://readdy.ai/api/search-image?query=children%20playing%20soccer%20on%20field%20coach%20training%20kids%20bright%20sunny%20day%20sports%20camp%20teamwork%20activities&width=800&height=600&seq=c4&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 5 ───────────────────────────────────────────────
  {
    id: 5,
    name: "Faith & Fun Camp",
    city: "Lawrenceville",
    ageRange: "5–11",
    ageGroup: "early-elementary",
    category: "faith",
    priceRange: "$175–$250/week",
    duration: "full-day",
    availableWeeks: [2, 4, 6, 8, 10],
    description: "Christian-based camp combining Bible stories, worship, outdoor games, and crafts in a warm, welcoming environment where every child feels loved.",
    website: "https://www.ymcaatlanta.org/camp",
    photo: "https://readdy.ai/api/search-image?query=children%20singing%20worship%20songs%20church%20camp%20happy%20kids%20hands%20raised%20faith%20based%20summer%20program%20bright%20indoor%20space&width=800&height=600&seq=c5&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 6 ───────────────────────────────────────────────
  {
    id: 6,
    name: "All Abilities Camp",
    city: "Duluth",
    ageRange: "5–17",
    ageGroup: "early-elementary",
    category: "special-needs",
    priceRange: "$300–$450/week",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Inclusive camp designed for children with special needs and their siblings. Trained staff provide individualized support and sensory-friendly activities.",
    website: "https://www.gwinnettcounty.com/departments/parksandrecreation/recreation/camps",
    photo: "https://readdy.ai/api/search-image?query=diverse%20children%20with%20special%20needs%20doing%20activities%20inclusive%20camp%20counselors%20helping%20adaptive%20equipment%20supportive%20environment&width=800&height=600&seq=c6&orientation=landscape",
    discount: true,
    availability: "Waitlist",
  },

  // ── CAMP 7 ───────────────────────────────────────────────
  {
    id: 7,
    name: "Teen Leadership Retreat",
    city: "Suwanee",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "outdoor",
    priceRange: "$400–$550/week",
    duration: "overnight",
    availableWeeks: [3, 4, 7, 8],
    description: "Overnight leadership development program with high ropes courses, community service projects, and college prep workshops for teens.",
    website: "https://www.ymcaatlanta.org/camp",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20on%20high%20ropes%20course%20leadership%20camp%20safety%20equipment%20outdoor%20adventure%20team%20building%20summer%20program&width=800&height=600&seq=c7&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 9 ───────────────────────────────────────────────
  {
    id: 9,
    name: "Basketball Skills Camp",
    city: "Lawrenceville",
    ageRange: "8–15",
    ageGroup: "upper-elementary",
    category: "sports",
    priceRange: "$275–$375/week",
    duration: "full-day",
    availableWeeks: [2, 4, 6, 8, 10],
    description: "Intensive basketball training focusing on fundamentals, game strategy, and daily scrimmages. Video analysis sessions help players level up their game.",
    website: "https://atlanta.clubscikidz.com",
    photo: "https://readdy.ai/api/search-image?query=kids%20playing%20basketball%20indoor%20court%20coach%20teaching%20dribbling%20skills%20sports%20camp%20training%20session%20summer%20program&width=800&height=600&seq=c9&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 10 ──────────────────────────────────────────────
  {
    id: 10,
    name: "Science Discovery Lab",
    city: "Duluth",
    ageRange: "6–12",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "$280–$380/week",
    duration: "full-day",
    availableWeeks: [1, 3, 5, 6, 7, 9, 10],
    description: "Hands-on science experiments exploring chemistry, physics, and biology. Every camper takes home a project they built themselves.",
    website: "https://atlanta.clubscikidz.com",
    photo: "https://readdy.ai/api/search-image?query=children%20doing%20science%20experiments%20lab%20coats%20test%20tubes%20colorful%20liquids%20bright%20laboratory%20setting%20STEM%20camp&width=800&height=600&seq=c10&orientation=landscape",
    discount: false,
    availability: "Registration Opens Soon",
  },

  // ── CAMP 11 ──────────────────────────────────────────────
  {
    id: 11,
    name: "Tween Adventure Week",
    city: "Suwanee",
    ageRange: "11–13",
    ageGroup: "tweens",
    category: "outdoor",
    priceRange: "$300–$400/week",
    duration: "full-day",
    availableWeeks: [5, 6, 7, 8],
    description: "Action-packed week for tweens featuring zip-lining, rock climbing, kayaking, and team challenges in a safe, supervised environment.",
    website: "https://adrenalineclimbing.com",
    photo: "https://readdy.ai/api/search-image?query=tweens%20zip%20lining%20through%20trees%20safety%20gear%20outdoor%20adventure%20camp%20exciting%20activity%20summer%20program&width=800&height=600&seq=c11&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 12 ──────────────────────────────────────────────
  {
    id: 12,
    name: "Musical Theater Workshop",
    city: "Buford",
    ageRange: "8–14",
    ageGroup: "upper-elementary",
    category: "arts",
    priceRange: "$325–$425/week",
    duration: "full-day",
    availableWeeks: [2, 3, 6, 7, 9, 10],
    description: "Full production musical theater camp with voice training, choreography, and acting classes — culminating in a Friday performance for families.",
    website: "https://alliancetheatre.org/education",
    photo: "https://readdy.ai/api/search-image?query=children%20rehearsing%20musical%20theater%20stage%20costumes%20choreography%20dance%20moves%20performing%20arts%20camp%20summer%20program&width=800&height=600&seq=c12&orientation=landscape",
    discount: true,
    availability: "Limited Spots",
  },

  // ── CAMP 13 ──────────────────────────────────────────────
  {
    id: 13,
    name: "The Little Gym of Buford Summer Camp",
    city: "Buford",
    ageRange: "3–6",
    ageGroup: "preschool",
    category: "sports",
    priceRange: "$200–$300/week",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Fun-filled camp featuring gymnastics, obstacle courses, games, and themed activities designed for younger kids.",
    website: "https://www.thelittlegym.com",
    photo: "https://readdy.ai/api/search-image?query=preschool%20children%20doing%20gymnastics%20obstacle%20course%20colorful%20gym%20equipment%20active%20movement%20camp%20instructor%20helping%20young%20kids%20bright%20indoor%20facility&width=800&height=600&seq=c13&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 14 ──────────────────────────────────────────────
  {
    id: 14,
    name: "My Gym Summer Camp",
    city: "Suwanee",
    ageRange: "3–5",
    ageGroup: "preschool",
    category: "sports",
    priceRange: "$180–$280/week",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Energetic camp focused on movement, games, and confidence-building activities for preschoolers.",
    website: "https://www.mygym.com",
    photo: "https://readdy.ai/api/search-image?query=preschoolers%20playing%20movement%20games%20colorful%20gym%20mats%20active%20play%20confidence%20building%20activities%20young%20children%20energetic%20summer%20camp&width=800&height=600&seq=c14&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 16 ──────────────────────────────────────────────
  {
    id: 16,
    name: "Club SciKidz Jr. Science Camp",
    city: "Gwinnett County",
    ageRange: "4–6",
    ageGroup: "preschool",
    category: "stem",
    priceRange: "$220–$320/week",
    duration: "half-day",
    availableWeeks: [2, 3, 4, 5, 6, 7, 8, 9],
    description: "A fun introduction to science with simple experiments, building activities, and discovery-based learning.",
    website: "https://atlanta.clubscikidz.com",
    photo: "https://readdy.ai/api/search-image?query=preschool%20children%20doing%20simple%20science%20experiments%20colorful%20materials%20discovery%20learning%20young%20kids%20building%20activities%20STEM%20camp&width=800&height=600&seq=c16&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 17 ──────────────────────────────────────────────
  {
    id: 17,
    name: "Chattahoochee Nature Center Little Explorers Camp",
    city: "Roswell",
    ageRange: "4–6",
    ageGroup: "preschool",
    category: "outdoor",
    priceRange: "$210–$290/week",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Nature-based camp where kids explore wildlife, plants, and outdoor discovery activities.",
    website: "https://www.chattnaturecenter.org",
    photo: "https://readdy.ai/api/search-image?query=preschool%20children%20exploring%20nature%20wildlife%20plants%20young%20kids%20nature%20center%20learning%20activities%20summer%20camp&width=800&height=600&seq=c17&orientation=landscape",
    discount: false,
    availability: "Registration Opens Soon",
  },

  // ── CAMP 18 ──────────────────────────────────────────────
  {
    id: 18,
    name: "Zoo Atlanta Preschool Safari Camp",
    city: "Atlanta",
    ageRange: "4–5",
    ageGroup: "preschool",
    category: "outdoor",
    priceRange: "$240–$340/week",
    duration: "half-day",
    availableWeeks: [2, 3, 4, 5, 6, 7, 8],
    description: "A themed camp where preschoolers learn about animals through games, crafts, and zoo exploration.",
    website: "https://www.zooatlanta.org",
    photo: "https://readdy.ai/api/search-image?query=preschool%20children%20at%20zoo%20learning%20about%20animals%20safari%20themed%20activities%20young%20kids%20crafts%20games%20zoo%20exploration%20summer%20camp&width=800&height=600&seq=c18&orientation=landscape",
    discount: false,
    availability: "Waitlist",
  },

  // ── CAMP 19 ──────────────────────────────────────────────
  {
    id: 19,
    name: "Primrose School Summer Adventure Club",
    city: "Multiple Gwinnett Locations",
    ageRange: "3–5",
    ageGroup: "preschool",
    category: "outdoor",
    priceRange: "$170–$250/week",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Play-based summer program with themed weekly activities and social learning opportunities.",
    website: "https://www.primroseschools.com",
    photo: "https://readdy.ai/api/search-image?query=preschool%20children%20playing%20themed%20activities%20social%20learning%20play-based%20program%20young%20kids%20summer%20adventure%20bright%20classroom&width=800&height=600&seq=c19&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 20 ──────────────────────────────────────────────
  {
    id: 20,
    name: "The Goddard School Summer Camp",
    city: "Buford",
    ageRange: "3–6",
    ageGroup: "preschool",
    category: "outdoor",
    priceRange: "$180–$270/week",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Preschool summer program combining play, creativity, and early learning experiences.",
    website: "https://www.goddardschool.com",
    photo: "https://readdy.ai/api/search-image?query=preschool%20children%20playing%20creative%20activities%20early%20learning%20play-based%20program%20young%20kids%20summer%20camp%20bright%20colorful%20classroom&width=800&height=600&seq=c20&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 21 ──────────────────────────────────────────────
  {
    id: 21,
    name: "Suwanee Sports Academy Camp",
    city: "Suwanee, GA",
    ageRange: "5–7",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Multi-sport camp offering a variety of athletic activities to build coordination, teamwork, and a love of movement in early elementary kids.",
    website: "https://www.ssasports.com",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20ages%205%20to%207%20playing%20multiple%20sports%20on%20field%20with%20coaches%20sunny%20day%20colorful%20jerseys%20teamwork%20early%20elementary%20sports%20camp%20outdoor%20activities&width=800&height=600&seq=c21&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 22 ──────────────────────────────────────────────
  {
    id: 22,
    name: "TOCA Soccer Camp",
    city: "Suwanee, GA",
    ageRange: "5–7",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Innovative soccer training using TOCA\'s signature technology and drills to develop ball skills, confidence, and a passion for the game.",
    website: "https://www.tocafootball.com",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20ages%205%20to%207%20practicing%20soccer%20drills%20on%20green%20field%20with%20coach%20bright%20sunny%20day%20colorful%20cones%20soccer%20balls%20early%20elementary%20training%20camp&width=800&height=600&seq=c22&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 23 ──────────────────────────────────────────────
  {
    id: 23,
    name: "Club SciKidz STEM Camp",
    city: "Atlanta Area",
    ageRange: "5–7",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Engaging STEM camp where early elementary kids explore science, technology, engineering, and math through fun hands-on experiments and projects.",
    website: "https://atlanta.clubscikidz.com",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20ages%205%20to%207%20doing%20science%20experiments%20colorful%20materials%20bright%20classroom%20STEM%20activities%20building%20projects%20early%20elementary%20summer%20camp&width=800&height=600&seq=c23&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 24 ──────────────────────────────────────────────
  {
    id: 24,
    name: "Engineering for Kids Camp",
    city: "Gwinnett County",
    ageRange: "5–7",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Hands-on engineering challenges designed for young learners — kids build, design, and problem-solve their way through exciting STEM activities.",
    website: "https://www.engineeringforkids.com",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20ages%205%20to%207%20building%20structures%20with%20blocks%20and%20engineering%20materials%20bright%20classroom%20instructor%20guiding%20early%20elementary%20STEM%20camp&width=800&height=600&seq=c24&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 25 ──────────────────────────────────────────────
  {
    id: 25,
    name: "Suwanee Academy of the Arts Camp",
    city: "Suwanee, GA",
    ageRange: "5–7",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Creative arts camp where young children explore drawing, painting, music, and movement in a nurturing and inspiring studio environment.",
    website: "https://www.suwaneeacademy.com",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20ages%205%20to%207%20painting%20and%20doing%20art%20projects%20colorful%20art%20studio%20bright%20space%20creative%20activities%20early%20elementary%20arts%20summer%20camp&width=800&height=600&seq=c25&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 26 ──────────────────────────────────────────────
  {
    id: 26,
    name: "The Actor\'s Scene Drama Camp",
    city: "Buford, GA",
    ageRange: "5–7",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Theater camp where kids discover the joy of performing through acting games, storytelling, improvisation, and a fun end-of-week showcase.",
    website: "https://www.theactorsscene.com",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20ages%205%20to%207%20performing%20on%20stage%20theater%20costumes%20acting%20drama%20camp%20bright%20stage%20lights%20early%20elementary%20performing%20arts%20summer%20program&width=800&height=600&seq=c26&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 27 ──────────────────────────────────────────────
  {
    id: 27,
    name: "Camp Invention",
    city: "Suwanee, GA",
    ageRange: "8–10",
    ageGroup: "upper-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Award-winning STEM camp where kids design, build, and invent using real-world challenges that spark creativity and critical thinking.",
    website: "https://www.invent.org/camp-invention",
    photo: "https://readdy.ai/api/search-image?query=children%20ages%208%20to%2010%20building%20inventions%20with%20materials%20bright%20classroom%20STEM%20camp%20engineering%20design%20challenge%20upper%20elementary%20summer%20program%20creative%20projects&width=800&height=600&seq=c27&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 28 ──────────────────────────────────────────────
  {
    id: 28,
    name: "Code Ninjas Summer Camp",
    city: "Buford / Suwanee",
    ageRange: "8–10",
    ageGroup: "upper-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Kids learn to code by building their own video games in a fun, ninja-themed environment with step-by-step progression and expert instructors.",
    website: "https://www.codeninjas.com",
    photo: "https://readdy.ai/api/search-image?query=children%20ages%208%20to%2010%20coding%20on%20computers%20game%20development%20bright%20modern%20classroom%20coding%20camp%20upper%20elementary%20summer%20program%20technology%20learning&width=800&height=600&seq=c28&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 29 ──────────────────────────────────────────────
  {
    id: 29,
    name: "Gwinnett Environmental & Heritage Center Camp",
    city: "Buford, GA",
    ageRange: "8–10",
    ageGroup: "upper-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Hands-on nature and science camp exploring local ecosystems, wildlife, and environmental stewardship through outdoor discovery and experiments.",
    website: "https://www.gwinnettehc.org",
    photo: "https://readdy.ai/api/search-image?query=children%20ages%208%20to%2010%20exploring%20nature%20trails%20environmental%20center%20wildlife%20science%20outdoor%20discovery%20upper%20elementary%20summer%20camp%20Georgia%20heritage&width=800&height=600&seq=c29&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 30 ──────────────────────────────────────────────
  {
    id: 30,
    name: "Gwinnett County Parks & Recreation Camp",
    city: "Gwinnett County",
    ageRange: "8–10",
    ageGroup: "upper-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Traditional day camp with sports, outdoor games, arts and crafts, and field trips — a classic summer experience for kids in Gwinnett County.",
    website: "https://www.gwinnettcounty.com/departments/parksandrecreation/recreation/camps",
    photo: "https://readdy.ai/api/search-image?query=children%20ages%208%20to%2010%20playing%20outdoor%20games%20sports%20activities%20nature%20exploration%20traditional%20day%20camp%20parks%20recreation%20sunny%20summer%20activities%20Gwinnett%20County%20upper%20elementary&width=800&height=600&seq=c30&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 31 ──────────────────────────────────────────────
  {
    id: 31,
    name: "Atlanta United Soccer Camp",
    city: "Gwinnett Area",
    ageRange: "11–13",
    ageGroup: "tweens",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Official Atlanta United soccer camp delivering professional-level training, tactical drills, and competitive scrimmages for tween players looking to elevate their game.",
    website: "https://www.atlutd.com",
    photo: "https://readdy.ai/api/search-image?query=tweens%20ages%2011%20to%2013%20playing%20competitive%20soccer%20on%20professional%20field%20Atlanta%20United%20branded%20training%20cones%20jerseys%20coach%20directing%20drills%20sunny%20day%20sports%20camp&width=800&height=600&seq=c31&orientation=landscape",
    discount: false,
    availability: "Waitlist",
  },

  // ── CAMP 32 ──────────────────────────────────────────────
  {
    id: 32,
    name: "Buford Elite Sports Training Camp",
    city: "Buford, GA",
    ageRange: "11–13",
    ageGroup: "tweens",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "High-performance multi-sport training camp focused on athleticism, speed, agility, and sport-specific skills for competitive tween athletes.",
    website: "https://www.bufordelitesportstraining.com",
    photo: "https://readdy.ai/api/search-image?query=tweens%20ages%2011%20to%2013%20elite%20sports%20training%20agility%20drills%20cones%20athletic%20field%20coach%20instructing%20competitive%20athletes%20summer%20sports%20camp%20Buford%20Georgia&width=800&height=600&seq=c32&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 33 ──────────────────────────────────────────────
  {
    id: 33,
    name: "Lavner Tech Revolution Camp",
    city: "Atlanta Area",
    ageRange: "11–13",
    ageGroup: "tweens",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Cutting-edge tech camp where tweens dive into coding, game design, app development, and cybersecurity through project-based learning and real-world challenges.",
    website: "https://www.lavnercampsandprograms.com/location/atlanta-ga-summer-camps-tech-camps-stem-camps-",
    photo: "https://readdy.ai/api/search-image?query=tweens%20ages%2011%20to%2013%20coding%20on%20laptops%20modern%20tech%20classroom%20game%20design%20app%20development%20cybersecurity%20STEM%20camp%20bright%20contemporary%20learning%20space%20summer%20program&width=800&height=600&seq=c33&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 34 ──────────────────────────────────────────────
  {
    id: 34,
    name: "Teen Leadership Camp",
    city: "Atlanta Area",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "YMCA-led leadership development camp empowering teens with communication skills, community service, and real-world challenges that build confidence and character.",
    website: "https://www.ymcaatlanta.org/camp",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20ages%2014%20to%2017%20leadership%20camp%20team%20building%20activities%20outdoor%20challenge%20course%20confident%20teens%20working%20together%20summer%20program%20Atlanta%20area%20bright%20sunny%20day&width=800&height=600&seq=c34&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 35 ──────────────────────────────────────────────
  {
    id: 35,
    name: "Adrenaline Climbing Summer Camp",
    city: "Suwanee, GA",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Thrilling rock climbing camp where teens master bouldering, top-rope, and lead climbing techniques in a safe, supportive environment with expert instructors.",
    website: "https://www.adrenalineclimbing.com",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20ages%2014%20to%2017%20rock%20climbing%20indoor%20climbing%20wall%20harness%20safety%20gear%20adventure%20camp%20Suwanee%20Georgia%20expert%20instructor%20coaching%20teens%20summer%20program&width=800&height=600&seq=c35&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 36 ──────────────────────────────────────────────
  {
    id: 36,
    name: "Georgia Tech Pre-College STEM Academy",
    city: "Atlanta, GA",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "overnight",
    availableWeeks: [3, 4, 5, 6, 7],
    description: "Immersive pre-college program on the Georgia Tech campus where high schoolers explore engineering, computer science, and research alongside university faculty and mentors.",
    website: "https://www.gatech.edu/academics/pre-college-programs",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20ages%2014%20to%2017%20on%20university%20campus%20pre-college%20STEM%20program%20engineering%20lab%20Georgia%20Tech%20college%20preparation%20summer%20academy%20bright%20modern%20research%20facility&width=800&height=600&seq=c36&orientation=landscape",
    discount: false,
    availability: "Waitlist",
  },

  // ── CAMP 37 ──────────────────────────────────────────────
  {
    id: 37,
    name: "Emory University Pre-College Program",
    city: "Atlanta, GA",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "overnight",
    availableWeeks: [4, 5, 6, 7, 8],
    description: "Rigorous academic summer experience at Emory University covering medicine, law, business, and the sciences — designed to prepare ambitious teens for college life.",
    website: "https://precollege.emory.edu",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20ages%2014%20to%2017%20on%20beautiful%20university%20campus%20pre-college%20academic%20program%20college%20preparation%20summer%20residential%20program%20Emory%20Atlanta%20Georgia%20ivy-style%20buildings&width=800&height=600&seq=c37&orientation=landscape",
    discount: false,
    availability: "Registration Opens Soon",
  },

  // ── CAMP 38 ──────────────────────────────────────────────
  {
    id: 38,
    name: "Atlanta Film & Media Arts Intensive",
    city: "Atlanta, GA",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [2, 3, 4, 5, 6, 7, 8],
    description: "Professional-level film, photography, and digital media intensive where teens write, direct, and produce their own short films with industry-grade equipment and mentors.",
    website: "https://www.atlantafilmfestival.com/education",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20ages%2014%20to%2017%20filming%20with%20professional%20camera%20equipment%20film%20production%20set%20director%20clapperboard%20media%20arts%20intensive%20summer%20program%20Atlanta%20Georgia%20creative%20studio&width=800&height=600&seq=c38&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 39 ──────────────────────────────────────────────
  {
    id: 39,
    name: "Alliance Theatre Teen Acting Conservatory",
    city: "Atlanta, GA",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Intensive performing arts conservatory at Atlanta\'s premier professional theater, offering advanced acting, voice, movement, and scene study with working theater professionals.",
    website: "https://www.alliancetheatre.org/education",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20ages%2014%20to%2017%20performing%20on%20professional%20theater%20stage%20acting%20conservatory%20dramatic%20scene%20study%20spotlight%20stage%20lights%20performing%20arts%20intensive%20summer%20program%20Atlanta&width=800&height=600&seq=c39&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 40 ──────────────────────────────────────────────
  {
    id: 40,
    name: "IMG Academy Sports Performance Camp",
    city: "Gwinnett Area",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [2, 3, 4, 5, 6, 7, 8, 9],
    description: "Elite sports performance academy offering sport-specific training in football, basketball, soccer, and tennis alongside strength & conditioning and mental performance coaching.",
    website: "https://www.imgacademy.com",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20ages%2014%20to%2017%20elite%20sports%20performance%20training%20athletic%20facility%20weight%20room%20speed%20agility%20drills%20competitive%20high%20school%20athletes%20summer%20sports%20academy%20program&width=800&height=600&seq=c40&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 41 ──────────────────────────────────────────────
  {
    id: 41,
    name: "Sprayberry High School Football Camp",
    city: "Marietta, GA",
    ageRange: "14–17",
    ageGroup: "teens",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [5, 6, 7, 8],
    description: "Competitive football skills camp run by experienced high school and college coaches, covering offensive and defensive fundamentals, film study, and position-specific drills.",
    website: "https://www.cobbk12.org/sprayberry",
    photo: "https://readdy.ai/api/search-image?query=teenagers%20ages%2014%20to%2017%20football%20training%20camp%20on%20field%20helmets%20pads%20coach%20instructing%20position%20drills%20competitive%20high%20school%20football%20summer%20skills%20camp%20Georgia%20sunny%20day&width=800&height=600&seq=c41&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 42 ──────────────────────────────────────────────
  {
    id: 42,
    name: "D1 Training Summer Camp",
    city: "Buford, GA",
    ageRange: "8–17",
    ageGroup: "upper-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Elite athletic training program focusing on speed, strength, agility, and sport-specific performance for serious young athletes.",
    website: "https://www.d1training.com/buford",
    photo: "https://readdy.ai/api/search-image?query=young%20athletes%20training%20in%20modern%20fitness%20facility%20speed%20agility%20drills%20strength%20conditioning%20D1%20training%20professional%20sports%20performance%20camp%20bright%20gym%20equipment&width=800&height=600&seq=c42&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 43 ──────────────────────────────────────────────
  {
    id: 43,
    name: "Georgia Swarm Lacrosse Camp",
    city: "Duluth, GA",
    ageRange: "8–17",
    ageGroup: "upper-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Professional lacrosse training with Georgia Swarm coaches teaching stick skills, game strategy, and competitive play.",
    website: "https://georgiaswarm.com",
    photo: "https://readdy.ai/api/search-image?query=young%20athletes%20playing%20lacrosse%20on%20field%20with%20professional%20coaches%20Georgia%20Swarm%20branded%20equipment%20stick%20skills%20training%20sunny%20day%20sports%20camp&width=800&height=600&seq=c43&orientation=landscape",
    discount: false,
    availability: "Registration Opens Soon",
  },

  // ── CAMP 44 ──────────────────────────────────────────────
  {
    id: 44,
    name: "Atlanta Silverbacks Soccer Camp",
    city: "Suwanee, GA",
    ageRange: "6–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Professional soccer training from Atlanta Silverbacks coaches focusing on technical skills, tactical awareness, and competitive match play.",
    website: "https://www.atlantasilverbacksfc.com",
    photo: "https://readdy.ai/api/search-image?query=children%20playing%20soccer%20on%20professional%20field%20Atlanta%20Silverbacks%20training%20session%20coaches%20teaching%20technical%20skills%20sunny%20day%20youth%20soccer%20camp&width=800&height=600&seq=c44&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 45 ──────────────────────────────────────────────
  {
    id: 45,
    name: "Atlanta Hawks Basketball Camp",
    city: "Various Metro Locations",
    ageRange: "8–17",
    ageGroup: "upper-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Official Atlanta Hawks basketball camp with professional coaching, skill development, and competitive games for all levels.",
    website: "https://www.hawks.com/youth-basketball",
    photo: "https://readdy.ai/api/search-image?query=young%20basketball%20players%20on%20indoor%20court%20Atlanta%20Hawks%20branded%20training%20professional%20coaches%20teaching%20skills%20drills%20competitive%20youth%20basketball%20camp&width=800&height=600&seq=c45&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 46 ──────────────────────────────────────────────
  {
    id: 46,
    name: "Top Golf Summer Academy",
    city: "Buford, GA",
    ageRange: "8–17",
    ageGroup: "upper-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Fun golf training combining technology and traditional instruction to develop swing mechanics, accuracy, and course management skills.",
    website: "https://topgolf.com",
    photo: "https://readdy.ai/api/search-image?query=young%20golfers%20at%20TopGolf%20facility%20modern%20driving%20range%20technology%20screens%20golf%20instruction%20summer%20academy%20youth%20training%20bright%20outdoor%20bays&width=800&height=600&seq=c46&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 47 ──────────────────────────────────────────────
  {
    id: 47,
    name: "Suwanee Sports Academy Summer Camp",
    city: "Suwanee, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Multi-sport summer camp offering basketball, soccer, baseball, and more with professional coaching and skill development.",
    website: "https://suwaneesportsacademy.com",
    photo: "https://readdy.ai/api/search-image?query=children%20playing%20multiple%20sports%20at%20sports%20academy%20facility%20basketball%20soccer%20baseball%20coaches%20teaching%20multi-sport%20summer%20camp%20bright%20athletic%20complex&width=800&height=600&seq=c47&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 48 ──────────────────────────────────────────────
  {
    id: 48,
    name: "Code Wiz Coding Camp",
    city: "Suwanee, GA",
    ageRange: "7–17",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Comprehensive coding camp teaching programming languages, game development, and app creation through project-based learning.",
    website: "https://codewiz.com",
    photo: "https://readdy.ai/api/search-image?query=children%20coding%20on%20computers%20modern%20classroom%20programming%20game%20development%20app%20creation%20STEM%20camp%20bright%20technology%20learning%20space&width=800&height=600&seq=c48&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 49 ──────────────────────────────────────────────
  {
    id: 49,
    name: "Snapology STEAM Camp",
    city: "Johns Creek, GA",
    ageRange: "5–12",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Hands-on STEAM learning using LEGO bricks, robotics, and technology to explore engineering, coding, and creative problem-solving.",
    website: "https://www.snapology.com",
    photo: "https://readdy.ai/api/search-image?query=children%20building%20with%20LEGO%20bricks%20robotics%20projects%20STEAM%20camp%20colorful%20blocks%20technology%20learning%20hands-on%20engineering%20activities%20bright%20classroom&width=800&height=600&seq=c49&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 50 ──────────────────────────────────────────────
  {
    id: 50,
    name: "Bricks 4 Kidz LEGO Camp",
    city: "Gwinnett County",
    ageRange: "5–12",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "LEGO-based engineering camp where kids build motorized models, learn basic robotics, and explore STEM concepts through play.",
    website: "https://www.bricks4kidz.com",
    photo: "https://readdy.ai/api/search-image?query=children%20building%20motorized%20LEGO%20models%20engineering%20camp%20robotics%20STEM%20learning%20colorful%20bricks%20hands-on%20construction%20activities%20bright%20classroom&width=800&height=600&seq=c50&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 51 ──────────────────────────────────────────────
  {
    id: 51,
    name: "iCode Summer Tech Camp",
    city: "Duluth, GA",
    ageRange: "7–17",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Technology camp teaching coding, robotics, game design, and digital creativity through engaging, project-based curriculum.",
    website: "https://icodeschool.com",
    photo: "https://readdy.ai/api/search-image?query=students%20learning%20coding%20robotics%20game%20design%20technology%20camp%20modern%20computers%20digital%20creativity%20STEM%20education%20bright%20contemporary%20classroom&width=800&height=600&seq=c51&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 52 ──────────────────────────────────────────────
  {
    id: 52,
    name: "Sylvan Learning STEM Camp",
    city: "Buford, GA",
    ageRange: "6–14",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Educational STEM camp combining science experiments, math challenges, and technology projects to keep skills sharp over summer.",
    website: "https://www.sylvanlearning.com",
    photo: "https://readdy.ai/api/search-image?query=children%20doing%20science%20experiments%20math%20challenges%20technology%20projects%20educational%20STEM%20camp%20bright%20learning%20center%20summer%20program&width=800&height=600&seq=c52&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 53 ──────────────────────────────────────────────
  {
    id: 53,
    name: "Dance 411 Summer Dance Camp",
    city: "Duluth, GA",
    ageRange: "5–17",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Comprehensive dance camp offering ballet, jazz, hip-hop, and contemporary styles with professional instructors and end-of-week showcase.",
    website: "https://dance411.com",
    photo: "https://readdy.ai/api/search-image?query=young%20dancers%20in%20dance%20studio%20practicing%20ballet%20jazz%20hip-hop%20contemporary%20dance%20camp%20professional%20instruction%20bright%20studio%20mirrors%20summer%20program&width=800&height=600&seq=c53&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 54 ──────────────────────────────────────────────
  {
    id: 54,
    name: "Atlanta School of Ballet Camp",
    city: "Johns Creek, GA",
    ageRange: "6–17",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Classical ballet training camp with professional instructors teaching technique, choreography, and performance skills.",
    website: "https://atlantaschoolofballet.com",
    photo: "https://readdy.ai/api/search-image?query=young%20ballet%20dancers%20in%20classical%20training%20studio%20professional%20instruction%20technique%20practice%20elegant%20dance%20studio%20summer%20ballet%20camp&width=800&height=600&seq=c54&orientation=landscape",
    discount: false,
    availability: "Registration Opens Soon",
  },

  // ── CAMP 55 ──────────────────────────────────────────────
  {
    id: 55,
    name: "Masterpiece Mixers Art Camp",
    city: "Johns Creek, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Creative art camp exploring painting, drawing, sculpture, and mixed media with professional artists in a fun, inspiring environment.",
    website: "https://masterpiecemixers.com",
    photo: "https://readdy.ai/api/search-image?query=children%20creating%20art%20paintings%20drawings%20sculpture%20mixed%20media%20colorful%20art%20studio%20creative%20camp%20bright%20artistic%20space%20summer%20program&width=800&height=600&seq=c55&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 56 ──────────────────────────────────────────────
  {
    id: 56,
    name: "Young Rembrandts Art Camp",
    city: "Gwinnett County",
    ageRange: "5–12",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Drawing-focused art camp teaching fundamental skills, techniques, and creative expression through structured lessons and projects.",
    website: "https://youngrembrandts.com",
    photo: "https://readdy.ai/api/search-image?query=children%20learning%20drawing%20techniques%20art%20instruction%20pencils%20paper%20creative%20expression%20art%20camp%20bright%20studio%20summer%20program&width=800&height=600&seq=c56&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 57 ──────────────────────────────────────────────
  {
    id: 57,
    name: "Creative ClayHouse Camp",
    city: "Duluth, GA",
    ageRange: "6–14",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Pottery and ceramics camp where kids learn hand-building, wheel-throwing, glazing, and firing techniques to create unique art pieces.",
    website: "https://creativeclayhouse.com",
    photo: "https://readdy.ai/api/search-image?query=children%20working%20with%20clay%20pottery%20wheel%20ceramics%20studio%20hand-building%20techniques%20creative%20art%20camp%20bright%20pottery%20studio%20summer%20program&width=800&height=600&seq=c57&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 58 ──────────────────────────────────────────────
  {
    id: 58,
    name: "Sawnee Mountain Preserve Camp",
    city: "Cumming, GA",
    ageRange: "6–14",
    ageGroup: "early-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Nature-based camp exploring hiking trails, wildlife observation, environmental education, and outdoor adventure activities.",
    website: "https://forsythco.com/parks",
    photo: "https://readdy.ai/api/search-image?query=children%20hiking%20mountain%20trails%20nature%20exploration%20wildlife%20observation%20outdoor%20adventure%20camp%20sunny%20day%20Georgia%20mountains%20summer%20program&width=800&height=600&seq=c58&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 59 ──────────────────────────────────────────────
  {
    id: 59,
    name: "Gwinnett County Parks & Recreation Camp",
    city: "Various Gwinnett Parks",
    ageRange: "6–14",
    ageGroup: "early-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Traditional outdoor day camp with sports, games, nature activities, arts and crafts, and weekly field trips.",
    website: "https://www.gwinnettcounty.com",
    photo: "https://readdy.ai/api/search-image?query=children%20playing%20outdoor%20games%20sports%20activities%20nature%20exploration%20traditional%20day%20camp%20parks%20recreation%20sunny%20summer%20day%20Gwinnett%20County&width=800&height=600&seq=c59&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 60 ──────────────────────────────────────────────
  {
    id: 60,
    name: "YMCA Adventure Camp",
    city: "Gwinnett County",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "YMCA day camp offering swimming, sports, outdoor games, arts and crafts, and character-building activities in a safe, fun environment.",
    website: "https://ymcaatlanta.org",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20YMCA%20camp%20swimming%20sports%20outdoor%20games%20arts%20and%20crafts%20traditional%20day%20camp%20sunny%20summer%20activities&width=800&height=600&seq=c60&orientation=landscape",
    discount: false,
    availability: "Limited Spots",
  },

  // ── CAMP 61 ──────────────────────────────────────────────
  {
    id: 61,
    name: "Treetop Quest Summer Camp",
    city: "Dunwoody, GA",
    ageRange: "8–17",
    ageGroup: "upper-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Adventure camp featuring zip-lining, aerial obstacle courses, climbing challenges, and team-building activities in a treetop setting.",
    website: "https://treetopquest.com",
    photo: "https://readdy.ai/api/search-image?query=young%20people%20on%20zipline%20aerial%20obstacle%20course%20treetop%20adventure%20camp%20safety%20harness%20outdoor%20challenge%20summer%20program%20forest%20canopy&width=800&height=600&seq=c61&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 62 ──────────────────────────────────────────────
  {
    id: 62,
    name: "Elevate Martial Arts Summer Camp",
    city: "Sugar Hill, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "An action-packed summer experience in Sugar Hill combining martial arts training with games, activities, and character education for a well-rounded summer experience.",
    website: "https://sugarhillsummercamp.com",
    registrationLink: "https://sparkpages.io/cart/2/?i=_5M8b&c=&ocu=",
    photo: "https://readdy.ai/api/search-image?query=children%20practicing%20martial%20arts%20karate%20taekwondo%20training%20discipline%20self-defense%20summer%20camp%20bright%20dojo%20instructor%20teaching&width=800&height=600&seq=c62&orientation=landscape",
    discount: true,
    availability: "Limited Spots",
  },

  // ── CAMP 63 ──────────────────────────────────────────────
  {
    id: 63,
    name: "Karate Atlanta Summer Camp",
    city: "Duluth, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Traditional karate camp focusing on technique, forms, sparring, and life skills like respect, focus, and perseverance.",
    website: "https://karateatlanta.com",
    photo: "https://readdy.ai/api/search-image?query=children%20in%20karate%20uniforms%20practicing%20forms%20techniques%20traditional%20martial%20arts%20training%20summer%20camp%20dojo%20instructor%20demonstrating&width=800&height=600&seq=c63&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 64 ──────────────────────────────────────────────
  {
    id: 64,
    name: "ATA Martial Arts Camp",
    city: "Buford, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "American Taekwondo Association camp teaching martial arts skills, self-defense, fitness, and positive life skills.",
    website: "https://ataonline.com",
    photo: "https://readdy.ai/api/search-image?query=children%20practicing%20taekwondo%20kicks%20martial%20arts%20training%20ATA%20uniforms%20summer%20camp%20bright%20dojo%20fitness%20self-defense&width=800&height=600&seq=c64&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 65 ──────────────────────────────────────────────
  {
    id: 65,
    name: "Tiger Academy Martial Arts Camp",
    city: "Suwanee, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Martial arts camp combining taekwondo training with games, activities, and character education for a well-rounded summer experience.",
    website: "https://tigeracademy.com",
    photo: "https://readdy.ai/api/search-image?query=children%20in%20martial%20arts%20training%20taekwondo%20practice%20games%20activities%20character%20education%20summer%20camp%20bright%20dojo%20fun%20learning&width=800&height=600&seq=c65&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 66 ──────────────────────────────────────────────
  {
    id: 66,
    name: "YMCA Summer Day Camp",
    city: "Gwinnett County",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Classic YMCA day camp with swimming, sports, arts and crafts, field trips, and character development activities.",
    website: "https://ymcaatlanta.org/camp/day-camp",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20YMCA%20day%20camp%20swimming%20pool%20sports%20activities%20arts%20and%20crafts%20traditional%20summer%20camp%20sunny%20day%20fun%20activities&width=800&height=600&seq=c66&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 67 ──────────────────────────────────────────────
  {
    id: 67,
    name: "Gwinnett County Parks Summer Camp",
    city: "Gwinnett County",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Traditional day camp offering sports, outdoor games, arts and crafts, and field trips — a classic summer experience for kids in Gwinnett County.",
    website: "https://www.gwinnettcounty.com/departments/parksandrecreation/recreation/camps",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20parks%20recreation%20day%20camp%20sports%20games%20crafts%20swimming%20traditional%20summer%20activities%20Gwinnett%20County%20sunny%20day&width=800&height=600&seq=c67&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 68 ──────────────────────────────────────────────
  {
    id: 68,
    name: "Camp All-American",
    city: "Johns Creek, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "All-American day camp featuring sports, swimming, arts, drama, nature activities, and weekly themed events.",
    website: "https://campallamerican.com",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20traditional%20American%20day%20camp%20sports%20swimming%20arts%20drama%20nature%20activities%20themed%20events%20summer%20fun%20sunny%20day&width=800&height=600&seq=c68&orientation=landscape",
    discount: false,
    availability: "Registration Opens Soon",
  },

  // ── CAMP 69 ──────────────────────────────────────────────
  {
    id: 69,
    name: "KidVentures Camp",
    city: "Johns Creek, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Adventure-themed day camp with weekly themes, field trips, sports, arts and crafts, and outdoor exploration.",
    website: "https://kidventurescamp.com",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20adventure%20themed%20day%20camp%20field%20trips%20sports%20arts%20and%20crafts%20outdoor%20exploration%20summer%20activities%20fun%20themed%20events&width=800&height=600&seq=c69&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 70 ──────────────────────────────────────────────
  {
    id: 70,
    name: "Camp Forte! Summer Music Camp",
    city: "Lawrenceville, GA",
    ageRange: "9–14",
    ageGroup: "tweens",
    category: "arts",
    priceRange: "$360/session ($180/week)",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8],
    description: "Give your child the gift of music! Four exciting 2-week sessions each focused on a different instrument or skill: Ukulele, Guitar, Voice, and Songwriting. Hands-on learning, music theory, aural skills, and a final performance recital. No prior experience needed — just a love for music!",
    website: "https://forte-music-academy25.square.site/",
    registrationLink: "https://docs.google.com/forms/d/e/1FAIpQLScGsKP_IXPDmTQcpkWFLWXCvcPs78oYwsKJsdTstSqOioNMjQ/viewform?usp=send_form",
    photo: "https://readdy.ai/api/search-image?query=children%20and%20tweens%20in%20music%20camp%20classroom%20learning%20guitar%20ukulele%20singing%20songwriting%20bright%20studio%20instruments%20on%20desks%20cheerful%20summer%20music%20program&width=800&height=600&seq=c70&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 71 ──────────────────────────────────────────────
  {
    id: 71,
    name: "Camp Invention – Sugar Hill Elementary",
    city: "Buford, GA",
    ageRange: "K–6 (Rising)",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "~$305 (discounts available)",
    duration: "full-day",
    availableWeeks: [3],
    description: "Award-winning STEM camp at Sugar Hill Elementary where kids design, invent, and engineer using real-world challenges. June 15–18, 2026 | 8:00 AM – 4:00 PM. Spark creativity, critical thinking, and a love of invention — no prior experience needed!",
    website: "https://invent.org/camp",
    registrationLink: "https://invent.org/camp",
    photo: "https://readdy.ai/api/search-image?query=elementary%20school%20children%20building%20inventions%20colorful%20hands-on%20STEM%20materials%20bright%20classroom%20invention%20challenge%20camp%20excited%20kids%20engineering%20design%20activities&width=800&height=600&seq=c71&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 72 ──────────────────────────────────────────────
  {
    id: 72,
    name: "Lanier Robotics STEM Camp",
    city: "Sugar Hill, GA",
    ageRange: "3rd–7th Grade",
    ageGroup: "upper-elementary",
    category: "stem",
    priceRange: "~$200",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Hands-on coding and engineering camp hosted at Lanier High School. Morning (8AM–12PM) or afternoon (1PM–5PM) sessions available. Students design, build, and program robots while developing problem-solving and computational thinking skills.",
    website: "#",
    photo: "https://readdy.ai/api/search-image?query=middle%20school%20students%20building%20and%20programming%20robots%20in%20bright%20school%20classroom%20STEM%20robotics%20camp%20coding%20engineering%20hands-on%20challenge%20competitive%20learning&width=800&height=600&seq=c72&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 73 ──────────────────────────────────────────────
  {
    id: 73,
    name: "Sugarloaf Performing Arts Camp",
    city: "Gwinnett Area",
    ageRange: "5–17",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "A vibrant summer camp offering dance, ballet, tumbling, and performance intensives for all levels. Students build technique, confidence, and stage presence in a supportive, inspiring studio environment.",
    website: "https://sugarloafperformingarts.org",
    registrationLink: "https://sugarloafperformingarts.org",
    photo: "https://readdy.ai/api/search-image?query=young%20dancers%20in%20bright%20dance%20studio%20performing%20ballet%20tumbling%20creative%20movement%20colorful%20costumes%20performing%20arts%20summer%20camp%20mirrors%20barres%20polished%20floors&width=800&height=600&seq=c73&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 74 ──────────────────────────────────────────────
  {
    id: 74,
    name: "Eye Candy Art Studio Camp",
    city: "Duluth, GA",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Immersive art studio camp where kids explore painting, clay sculpting, and mixed media creations. Professional artists guide campers through imaginative projects in a fun, colorful, hands-on environment.",
    website: "https://eyecandyartstudio.net",
    registrationLink: "https://eyecandyartstudio.net",
    photo: "https://readdy.ai/api/search-image?query=children%20painting%20on%20canvases%20and%20sculpting%20clay%20in%20bright%20colorful%20art%20studio%20mixed%20media%20projects%20creative%20summer%20camp%20professional%20art%20instruction&width=800&height=600&seq=c74&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 75 ──────────────────────────────────────────────
  {
    id: 75,
    name: "i9 Sports Summer Camp",
    city: "Multiple Gwinnett Locations",
    ageRange: "3–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "~$119/week",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Multi-sport camp offering basketball, soccer, flag football, and golf across multiple convenient locations. i9 Sports focuses on fun, fitness, and fundamental skills in a positive, no-pressure environment.",
    website: "https://i9sports.com",
    registrationLink: "https://i9sports.com",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20playing%20multiple%20sports%20basketball%20soccer%20flag%20football%20golf%20bright%20outdoor%20fields%20cheerful%20coaches%20team%20activities%20i9%20sports%20summer%20camp&width=800&height=600&seq=c75&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 76 ──────────────────────────────────────────────
  {
    id: 76,
    name: "Legacy MMA Adventure Camp",
    city: "Sugar Hill / Buford / Braselton / Duluth",
    ageRange: "5–14",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "~$119/week",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Action-packed week-long camps at four convenient locations featuring field trips, team sports, arts and crafts, and martial arts — all packed into one incredible adventure. Building confidence, discipline, and lifelong friendships!",
    website: "https://legacymmaclub.com",
    registrationLink: "https://legacymmaclub.com",
    photo: "https://readdy.ai/api/search-image?query=children%20in%20martial%20arts%20uniforms%20playing%20sports%20doing%20arts%20and%20crafts%20on%20field%20trip%20adventure%20camp%20multiple%20activities%20MMA%20training%20confident%20kids%20sunny%20day&width=800&height=600&seq=c76&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 77 ──────────────────────────────────────────────
  {
    id: 77,
    name: "Camp Southern Ground",
    city: "Fayetteville, GA",
    ageRange: "7–17",
    ageGroup: "upper-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "overnight",
    availableWeeks: [3, 4, 5, 6, 7, 8],
    description: "A world-class overnight camp founded by Zac Brown focused on outdoor adventure and powerful inclusion-based programming. Campers grow through challenges, connection, and a deep love of the great outdoors.",
    website: "https://campsouthernground.org",
    registrationLink: "https://campsouthernground.org",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20overnight%20camp%20outdoor%20adventure%20Georgia%20forest%20campfire%20team%20building%20inclusive%20summer%20camp%20program%20lake%20activities%20nature%20exploration&width=800&height=600&seq=c77&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 78 ──────────────────────────────────────────────
  {
    id: 78,
    name: "Camp Arrowhead",
    city: "NC Mountains",
    ageRange: "7–16 (Boys)",
    ageGroup: "upper-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "overnight",
    availableWeeks: [4, 5, 6, 7, 8],
    description: "A beloved boys overnight camp nestled in the North Carolina mountains. Campers explore hiking, swimming, canoeing, archery, and classic outdoor adventures while building lasting friendships and independence.",
    website: "https://camparrowhead.com",
    registrationLink: "https://camparrowhead.com",
    photo: "https://readdy.ai/api/search-image?query=boys%20at%20mountain%20overnight%20summer%20camp%20hiking%20canoeing%20archery%20swimming%20North%20Carolina%20forest%20lake%20cabins%20traditional%20outdoor%20adventure%20friendship&width=800&height=600&seq=c78&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 79 ──────────────────────────────────────────────
  {
    id: 79,
    name: "Camp Yaldi",
    city: "North Georgia",
    ageRange: "7–17",
    ageGroup: "upper-elementary",
    category: "faith",
    priceRange: "Contact for pricing",
    duration: "overnight",
    availableWeeks: [4, 5, 6, 7],
    description: "A faith-based outdoor overnight camp where kids connect with nature, grow in character, and build meaningful friendships in an inspiring outdoor setting rooted in faith and community.",
    website: "https://campyaldi.com",
    registrationLink: "https://campyaldi.com",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20faith%20based%20outdoor%20overnight%20camp%20North%20Georgia%20mountains%20worship%20campfire%20nature%20activities%20community%20building%20summer%20spiritual%20program&width=800&height=600&seq=c79&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 80 ──────────────────────────────────────────────
  {
    id: 80,
    name: "Camp Woodmont",
    city: "North Georgia",
    ageRange: "6–16",
    ageGroup: "early-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "overnight",
    availableWeeks: [3, 4, 5, 6, 7, 8],
    description: "A classic traditional overnight camp in the North Georgia mountains offering swimming, horseback riding, archery, arts and crafts, and campfire evenings. An authentic, screen-free summer experience kids never forget.",
    website: "https://campwoodmont.com",
    registrationLink: "https://campwoodmont.com",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20traditional%20overnight%20camp%20North%20Georgia%20mountains%20horseback%20riding%20archery%20crafts%20campfire%20classic%20summer%20camp%20experience%20cabins%20lake&width=800&height=600&seq=c80&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 81 ──────────────────────────────────────────────
  {
    id: 81,
    name: "Camp Glisson",
    city: "Dahlonega, GA",
    ageRange: "6–17",
    ageGroup: "early-elementary",
    category: "faith",
    priceRange: "Contact for pricing",
    duration: "overnight",
    availableWeeks: [3, 4, 5, 6, 7, 8],
    description: "A faith-based overnight camp in the Blue Ridge Mountains of North Georgia. Campers participate in worship, outdoor adventures, team challenges, and campfire devotionals in a community of belonging and joy.",
    website: "https://glisson.org",
    registrationLink: "https://glisson.org",
    photo: "https://readdy.ai/api/search-image?query=children%20at%20faith%20based%20overnight%20summer%20camp%20Blue%20Ridge%20Mountains%20Georgia%20worship%20outdoor%20adventures%20lake%20swimming%20campfire%20community%20church%20summer%20program&width=800&height=600&seq=c81&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 82 ──────────────────────────────────────────────
  {
    id: 82,
    name: "Science Akademia Camp",
    city: "Gwinnett Area",
    ageRange: "6–14",
    ageGroup: "early-elementary",
    category: "stem",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "A one-of-a-kind camp blending STEM learning with outdoor exploration. Campers conduct real science experiments, explore nature, and tackle engineering challenges — combining the best of lab and outdoor discovery.",
    website: "https://satapcs.com",
    registrationLink: "https://satapcs.com",
    photo: "https://readdy.ai/api/search-image?query=children%20doing%20science%20experiments%20outdoors%20hands-on%20STEM%20learning%20nature%20exploration%20engineering%20challenges%20bright%20sunny%20day%20combined%20indoor%20outdoor%20learning%20camp&width=800&height=600&seq=c82&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 83 ──────────────────────────────────────────────
  {
    id: 83,
    name: "Threaded from Heaven Sewing Camp",
    city: "Suwanee, GA",
    ageRange: "8–17",
    ageGroup: "upper-elementary",
    category: "arts",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "A unique specialty camp where kids and teens learn the art of sewing and fashion design. From stitching basics to creating original garments, campers leave with wearable projects and a brand-new creative skill set.",
    website: "https://threadedfromheaven.com",
    registrationLink: "https://threadedfromheaven.com",
    photo: "https://readdy.ai/api/search-image?query=young%20girls%20and%20boys%20learning%20sewing%20fashion%20design%20studio%20colorful%20fabric%20sewing%20machines%20creative%20projects%20garment%20making%20specialty%20summer%20camp%20bright%20studio&width=800&height=600&seq=c83&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 84 ──────────────────────────────────────────────
  {
    id: 84,
    name: "Valley View Ranch Equestrian Camp",
    city: "North Georgia",
    ageRange: "7–17",
    ageGroup: "upper-elementary",
    category: "outdoor",
    priceRange: "Contact for pricing",
    duration: "overnight",
    availableWeeks: [3, 4, 5, 6, 7, 8],
    description: "A dream come true for horse lovers! Valley View Ranch offers riding lessons, horse care, trail rides, and equestrian skill-building in a beautiful ranch setting. Campers bond with horses and build confidence every day.",
    website: "https://valleyviewranch.com",
    registrationLink: "https://valleyviewranch.com",
    photo: "https://readdy.ai/api/search-image?query=children%20horseback%20riding%20equestrian%20camp%20beautiful%20ranch%20setting%20trail%20ride%20horse%20care%20lessons%20green%20pastures%20barn%20summer%20camp%20North%20Georgia%20horses%20sunny&width=800&height=600&seq=c84&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 85 ──────────────────────────────────────────────
  {
    id: 85,
    name: "E Center Gym Basketball Camp",
    city: "Sugar Hill, GA",
    ageRange: "All Ages",
    ageGroup: "upper-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Co-Ed Morning Basketball Camp at the ECenter at Sugar Hill. A great opportunity for young athletes to sharpen their dribbling, passing, and game skills in a fun, structured morning setting with experienced coaches.",
    website: "http://ecenteratsugarhill.com",
    registrationLink: "http://ecenteratsugarhill.com",
    photo: "https://readdy.ai/api/search-image?query=children%20playing%20basketball%20indoors%20bright%20gym%20morning%20camp%20drills%20dribbling%20passing%20teamwork%20coach%20youth%20basketball%20training%20cheerful%20athletic%20facility%20summer%20program&width=800&height=600&seq=c85&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 86 ──────────────────────────────────────────────
  {
    id: 86,
    name: "Gary Pirkle Park Sports Camps",
    city: "Sugar Hill / Buford, GA",
    ageRange: "All Ages",
    ageGroup: "early-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "full-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "i9 Sports programs at Gary Pirkle Park offering Co-Ed Soccer, Co-Ed Flag Football, and a Girls-ONLY Flag Football league. Positive coaching, fun gameplay, and skill-building for all levels in a welcoming park setting.",
    website: "https://www.i9sports.com/programs/sugar-hill-buford-gary-pirkle-park-soccer-league-may-2026/176521",
    registrationLink: "https://www.i9sports.com/programs/sugar-hill-buford-gary-pirkle-park-soccer-league-may-2026/176521",
    photo: "https://readdy.ai/api/search-image?query=children%20playing%20soccer%20and%20flag%20football%20on%20green%20park%20field%20sunny%20day%20cheerful%20coaches%20team%20sports%20co-ed%20youth%20leagues%20summer%20sports%20program%20outdoor%20park&width=800&height=600&seq=c86&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 87 ──────────────────────────────────────────────
  {
    id: 87,
    name: "Sugar Hill Golf Course Golf Camp",
    city: "Sugar Hill, GA",
    ageRange: "All Ages",
    ageGroup: "upper-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Co-Ed Golf Camp at the Sugar Hill Golf Club hosted by the City of Sugar Hill Parks & Recreation. Young golfers learn swing mechanics, putting, course etiquette, and the fundamentals of the game on a beautiful local course.",
    website: "https://cityofsugarhill.com/services/parks-recreation/sugar-hill-golf-club/",
    registrationLink: "https://cityofsugarhill.com/services/parks-recreation/sugar-hill-golf-club/",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20learning%20golf%20on%20lush%20green%20golf%20course%20with%20instructor%20swing%20practice%20putting%20green%20cheerful%20youth%20golf%20camp%20sunny%20summer%20day%20Sugar%20Hill%20Georgia&width=800&height=600&seq=c87&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 88 ──────────────────────────────────────────────
  {
    id: 88,
    name: "Bogan Park Gym Basketball Camp",
    city: "Buford, GA",
    ageRange: "All Ages",
    ageGroup: "upper-elementary",
    category: "sports",
    priceRange: "Contact for pricing",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Co-Ed Basketball Camp at Bogan Park Gym through Gwinnett County Parks & Recreation. Players of all skill levels develop fundamentals, teamwork, and competitive game skills in a well-equipped county gym facility.",
    website: "https://www.gwinnettcounty.com/government/departments/parks-recreation/parks/bogan",
    registrationLink: "https://www.gwinnettcounty.com/government/departments/parks-recreation/parks/bogan",
    photo: "https://readdy.ai/api/search-image?query=young%20athletes%20playing%20co-ed%20basketball%20indoors%20bright%20county%20gym%20facility%20coaching%20drills%20team%20scrimmage%20youth%20camp%20Gwinnett%20County%20Parks%20Recreation%20summer%20program&width=800&height=600&seq=c88&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

  // ── CAMP 89 ──────────────────────────────────────────────
  {
    id: 89,
    name: "Little Starz",
    city: "Buford, GA",
    ageRange: "3–9",
    ageGroup: "preschool",
    category: "sports",
    priceRange: "~$200/week",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "Fun, active sports programs designed specifically for young kids ages 3–9. All camps are age-appropriate and focused on building movement skills, confidence, and a love of sports.",
    website: "https://littlestarzcamp.com",
    photo: "https://readdy.ai/api/search-image?query=young%20children%20ages%203%20to%209%20playing%20sports%20games%20on%20bright%20grassy%20field%20cheerful%20coaches%20colorful%20jerseys%20preschool%20early%20elementary%20active%20sports%20camp%20fun%20sunny%20day&width=800&height=600&seq=c89&orientation=landscape",
    discount: false,
    availability: "Spots Available",
  },

  // ── CAMP 90 ──────────────────────────────────────────────
  {
    id: 90,
    name: "J&K Play Starts",
    city: "Braselton, GA",
    ageRange: "2–5",
    ageGroup: "preschool",
    category: "outdoor",
    priceRange: "~$130/week",
    duration: "half-day",
    availableWeeks: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    description: "A nurturing play-based program for toddlers and preschoolers in Braselton. Focused on social development, imaginative play, and early movement skills in a safe, cheerful environment.",
    website: "https://www.jandkplaystarts.com",
    photo: "https://readdy.ai/api/search-image?query=toddlers%20and%20preschoolers%20ages%202%20to%205%20playing%20together%20colorful%20outdoor%20play%20area%20cheerful%20caregiver%20bright%20sunny%20day%20nurturing%20early%20childhood%20camp&width=800&height=600&seq=c90&orientation=landscape",
    discount: false,
    availability: "Unknown — Check with Camp",
  },

];